import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { project } from '@/lib/project';
import { TravelAPIClient } from '@/lib/travel-api-client';
import { sanitizeArticle } from '@/lib/utils';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import Link from 'next/link';

import {
	MdAccessibleForward,
	MdAirlineSeatReclineNormal,
	MdChecklist,
	MdChevronRight,
	MdFreeCancellation,
	MdLocalOffer,
	MdLoyalty,
	MdLuggage,
	MdPets,
	MdPublishedWithChanges,
	MdRoute,
	MdTrackChanges,
	MdVerified,
} from 'react-icons/md';
import { getCanonicalUrl, i18nRoute } from 'routes';

export function ArticleIcon(props) {
	const title = props.title;

	const icons = [
		{
			name: 'reward',
			icon: MdLoyalty,
		},
		{
			name: 'deals',
			icon: MdLocalOffer,
		},
		{
			name: 'discount',
			icon: MdLocalOffer,
		},
		{
			name: 'accessibility',
			icon: MdAccessibleForward,
		},
		{
			name: 'disabilities',
			icon: MdAccessibleForward,
		},
		{
			name: 'onboard',
			icon: MdChecklist,
		},
		{
			name: 'official',
			icon: MdVerified,
		},
		{
			name: 'oficial',
			icon: MdVerified,
		},
		{
			name: 'baggage',
			icon: MdLuggage,
		},
		{
			name: 'luggage',
			icon: MdLuggage,
		},
		{
			name: 'cancellation',
			icon: MdFreeCancellation,
		},
		{
			name: 'seat',
			icon: MdAirlineSeatReclineNormal,
		},
		{
			name: 'pet',
			icon: MdPets,
		},
		{
			name: 'track',
			icon: MdTrackChanges,
		},
		{
			name: 'changes',
			icon: MdPublishedWithChanges,
		},
		{
			name: 'route',
			icon: MdRoute,
		},
	];

	let Icon = MdChevronRight;
	let found = icons.find((icon) =>
		title.toLowerCase().includes(icon.name.toLowerCase())
	);
	if (found) {
		Icon = found.icon;
	}

	return <Icon {...props} title={null} />;
}

export function Article({ article }) {
	return (
		<li className="relative flex flex-col">
			<h2 className="text-base/7 flex items-center gap-x-3 font-semibold capitalize text-gray-900">
				<ArticleIcon
					title={article.slug}
					aria-hidden="true"
					className="size-5 flex-none text-blue-600"
				/>

				{article.title}
			</h2>
			<div className="text-base/7 mt-4 flex flex-auto flex-col text-gray-600">
				<p className="flex-auto">{article.excerpt}...</p>
				<p className="mt-6">
					<Link title={article.companyTitle} href={article.href} locale={false}>
						<span className="text-sm/6 font-semibold text-blue-600">
							{article.companyTitle} <span aria-hidden="true">→</span>
						</span>
					</Link>
				</p>
			</div>
		</li>
	);
}

export default function Page({ company, articles, locale }) {
	const { t } = useTranslation(['help_center', 'common']);
	const articlesNames = articles
		.map((article) => article.title)
		.slice(0, 3)
		.join(', ');

	const overtitle = t('help_center:overtitle');
	const title = t('help_center:company.title', { companyName: company.name });
	const description = t('help_center:company.description', {
		companyName: company.name,
		serviceNames: articlesNames,
	});
	const contactMessage = t('help_center:company.contact');
	const canonicalURL = getCanonicalUrl(`/help-center/[companyId]`, locale, {
		companyId: company.id,
	});

	const breadcrumbs = [
		{
			name: t('common:links.home.title'),
			href: i18nRoute('/', locale),
			locale,
		},
		{
			name: t(`common:links.help_center.title`),
			href: i18nRoute(`/help-center`, locale),
			current: false,
			locale,
		},
		{
			name: company.name,
			href: i18nRoute(`/help-center/[companyId]`, locale, {
				companyId: company.id,
			}),
			current: true,
			locale: locale,
		},
	];

	return (
		<>
			<NextSeo
				title={title}
				description={description}
				canonical={canonicalURL}
				additionalMetaTags={[
					{
						name: 'keywords',
						content: articlesNames,
					},
				]}
			/>

			<main>
				<section className="bg-blue-50 py-8 lg:py-20">
					<div className="mx-auto max-w-3xl px-4 lg:px-8 lg:text-center">
						<span className="text-base/7 font-semibold text-blue-600">
							{overtitle}
						</span>
						<h1 className="text-pretty lg:text-balance mt-2 text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
							{title}
						</h1>
						<p className="text-lg/8 mt-6 text-gray-600">
							{description} {contactMessage}
						</p>
					</div>
				</section>
				<div className="bg-white py-8 lg:py-20">
					<div className="mx-auto max-w-7xl px-4 lg:px-8">
						{/* {!hasLocaleArticles && (
							<div className="mt-8 border-l-4 border-blue-400 bg-blue-50 p-4">
								<p className="text-lg/8 text-gray-600">
									{t('help_center:missingTranslations')}
								</p>
							</div>
						)} */}
						<div className="mx-auto max-w-2xl lg:max-w-none">
							<ul className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
								{articles.map((article) => (
									<Article key={article.slug} article={article} />
								))}
							</ul>
						</div>
					</div>
				</div>
			</main>
			<Breadcrumbs pages={breadcrumbs} />
		</>
	);
}

export async function getStaticPaths({ locales }) {
	const locale = project.defaultLocale;
	const paths = [];

	return {
		paths: paths,
		fallback: 'blocking',
	};
}

export async function getStaticProps({ params, locale }) {
	const { companyId } = params;
	const travelAPIClient = new TravelAPIClient();

	const fullLocale = locale === 'en' ? 'en-US' : locale;

	const promises = [
		travelAPIClient.getCompany({ companyId, locale }),
		travelAPIClient.getCompanyContents({
			companyId,
			locale: fullLocale,
			cache: Date.now(),
		}),
	];
	const [company, articles] = await Promise.all(promises);

	if (!company || !articles || !articles.length) {
		return {
			notFound: true,
		};
	}

	articles.forEach((article) => sanitizeArticle({ company, article, locale }));

	return {
		props: {
			company,
			articles,
			locale,
			...(await serverSideTranslations(locale, ['help_center', 'common'])),
		},
	};
}
